import { defineComponent as _defineComponent } from 'vue'
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import type { Clip, ObjectType } from '@/@types/graphql-types';

import { ClipProvider, SraEventAction } from '@/@types/graphql-types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import YoutubePlayerGraphql from '@/ui-components/YoutubePlayerGraphql.vue';

import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { computed, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleClips',
  props: {
    clips: null,
    limit: { default: 4 },
    isBlocking: { type: Boolean },
    titleObjectType: null,
    titleObjectId: null,
    titleId: null,
    seasonNumber: null,
    episodeNumber: null,
    mute: { type: Boolean },
    autoplay: { type: Boolean },
    source: { default: '' },
    influencedSponsoredAd: null,
    additionalContexts: { default: () => [] },
    value: null
  },
  emits: ["ended", "input"],
  setup(__props: any, { expose, emit }: { emit: ({
	(e: 'ended'): void;
	// with vue 2.7 composition api we can't use custom names
	(e: 'input', activeClipId: string | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { clips: Clip[], limit: number, isBlocking?: boolean, titleObjectType?: ObjectType, titleObjectId?: number, titleId?: string, seasonNumber?: number, episodeNumber?: number, mute?: boolean, autoplay?: boolean, source: string, influencedSponsoredAd?: SponsoredAdFragment, additionalContexts: SnowplowContext[], value?: string };





const { trackSraEvent } = useBackendTracking();

//@Model('trailer-playing', { default: null }) readonly activeClipId!: string | null;

const trackingMap = {
	buffering: 'youtube_buffering',
	playing: 'youtube_playing',
	paused: 'youtube_paused',
};

const trackingProviders: Record<ClipProvider | 'PLEX', number> = {
	[ClipProvider.Youtube]: 1,
	[ClipProvider.Videobuster]: 2,
	[ClipProvider.Dailymotion]: 3,
	PLEX: 4,
};

const hasPlayedClips = ref<Record<string, boolean>>({});

const visibleClips = computed(() => props.clips.slice(0, props.limit));

const hasAutoplay = computed(() => props.limit === 1 && props.autoplay === true);

const playerVars = computed<YoutubePlayerOptions>(() => {
	return {
		autoplay: hasAutoplay.value ? 1 : 0,
		mute: props.mute ? 1 : 0,
	};
});

function isActiveClip(clip: Clip) {
	const id = props.value?.split(/:(.*)/s); // to split into only array of two strings.
	if (!id) return false;

	return id[0] === props.source && id[1] == clip.externalId;
}

function onActivate(clipId: string | null) {
	const id = clipId ? `${props.source}:${clipId}` : null;

	emit('input', id);
}

function onTrack(eventName: 'buffering' | 'playing' | 'paused' | 'ended', clip: Clip) {
	// for TrailerAutoplay module
	if (eventName === 'ended') {
		return emit('ended');
	}

	const category = trackingMap[eventName];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: props.titleObjectType?.toLowerCase(),
				label: props.source,
				nonInteraction: props.autoplay,
				value: trackingProviders[clip.provider],
			},
			props.additionalContexts
		);
	}

	// track youtube_started only once if the clip is played at least once
	if (eventName === 'playing') {
		// track influenced campaign metrics
		const influenced = !!props.influencedSponsoredAd?.bidId;

		if (influenced) {
			trackSraEvent({
				action: SraEventAction.PlayTrailerClicked,
				sponsoredAd: () => props.influencedSponsoredAd,
				jwEntityID: props.titleId,
			});
		}

		if (!(clip.externalId in hasPlayedClips.value)) {
			TrackingHelper.trackEvent(
				'youtube_started',
				{
					action: props.titleObjectType?.toLowerCase(),
					label: props.source,
					value: trackingProviders[clip.provider],
				},
				props.additionalContexts
			);
		}

		hasPlayedClips.value[clip.externalId] = true;
	}
}

expose({
	clips: props.clips,
	mute: props.mute,
	source: props.source,
	autoplay: props.autoplay,
	visibleClips,
	isActiveClip,
	onTrack,
});

return { __sfc: true,props, emit, trackSraEvent, trackingMap, trackingProviders, hasPlayedClips, visibleClips, hasAutoplay, playerVars, isActiveClip, onActivate, onTrack, YoutubePlayerGraphql }
}

})